"use client";

import { c as _c } from "react/compiler-runtime";
import type { MagnusUIMessage } from "@bg-bgi/magnus-sdk";
import { useToolRegistry } from "@bg-bgi/magnus-sdk/react";
import { Shimmer } from "@workspace/ui/components/ai-elements/shimmer";
import { Separator } from "@workspace/ui/components/separator";
import { cn } from "@workspace/ui/lib/utils";
import { AppWindowIcon, AudioLinesIcon } from "lucide-react";
import { Fragment, memo, type ReactNode, useState } from "react";
import { isPreviewableMediaType, useAttachmentPreview } from "./attachment-preview";
import { MessageAttachmentsScroll, SentAttachmentTile, type SentAttachmentTileProps } from "./composer/attachments";
import { MessageArtifactReference, MessageSkillReference } from "./composer/chips";
import { SkillsIcon } from "./composer/icons";
import { markdownForCopy } from "./copy";
import { MessageActions, type MessageFeedbackProps } from "./message-actions";
import { MessageResponse, SmoothTextBlock } from "./response";
import { CollapsibleToolGroup, findActiveToolGroupIndex, groupMessageParts, StandaloneReasoning } from "./tool-group";
import { ToolPart } from "./tool-part";
import { useExtraToolViews } from "./tool-views";
import { isVoiceSessionPart, VoiceSessionChip } from "./voice-session";

/** One message part, as the transcript sees it. */
export type MagnusChatMessagePart = MagnusUIMessage["parts"][number];
type MessagePart = MagnusChatMessagePart;

/**
 * Block-side mirror of the upload-processing wire shape (the services
 * package's `UploadProcessingSnapshot`). Declared here so the block never
 * imports `@workspace/*` server code — the host feeds live snapshots
 * through the transcript's `attachmentStatuses` prop.
 */
export interface MagnusChatProcessingSnapshot {
  richPassFailed?: boolean | null;
  status: string | null;
}

/** Native `file` parts and `data-magnus-document` parts shown as tiles. */
function isAttachmentPart(part: MessagePart): boolean {
  return part.type === "file" || part.type === "data-magnus-document";
}

/** Skill / artifact mention chips carried over from the rich composer. */
function isReferencePart(part: MessagePart): boolean {
  return part.type === "data-skill-reference" || part.type === "data-artifact-reference";
}

/** The user turn's bubble: a quiet container sized to its content. */
const USER_BUBBLE = "w-fit max-w-full min-w-0 wrap-anywhere rounded-lg bg-secondary px-4 py-3 text-foreground";

/** Compaction wire literals (ADR-054), matched structurally so the block
 *  stays host-agnostic — same idiom as `data-skill-reference` below. */
function isCompactionSummaryPart(part: MessagePart): boolean {
  return part.type === "data-compaction-summary";
}
function isCompactionContinuationPart(part: MessagePart): boolean {
  return part.type === "data-compaction-continuation";
}

/** Booking-form marker (ADR-085) — matched structurally like the compaction
 *  literals so the block stays host-agnostic. Its presence on a user turn
 *  means the visible text is a machine-composed booking instruction the user
 *  never typed, so the whole row is hidden. */
function isBookingFormPart(part: MessagePart): boolean {
  return part.type === "data-booking-form";
}

/** Retry affordance rule for sent attachments: hard failure, or the rich
 *  (tier-2) pass failed and needs a user-triggered retry. Matches the
 *  services package's `canRetryUploadProcessing`. */
export function isProcessingFailed(snapshot: MagnusChatProcessingSnapshot): boolean {
  return snapshot.richPassFailed === true || snapshot.status === "failed";
}

/**
 * What the tile's status dot should read. A tier-2 failure arrives as
 * `text-ready` + `richPassFailed`, which the dot would otherwise render as the
 * cheerful "Enhancing document…" — and the retry affordance only appears on
 * `failed`, so the user would be stuck looking at a permanent spinner. Mirrors
 * the services package's `getUploadIndicatorStatus`.
 */
function indicatorStatus(failed: boolean, liveStatus: string | null | undefined, data?: {
  processingStatus?: string | null;
}): string | null | undefined {
  if (failed) {
    return "failed";
  }
  return liveStatus ?? data?.processingStatus;
}

/**
 * Wrap raw markdown in a fenced code block so the existing Streamdown
 * pipeline renders it as highlighted source. The fence is one backtick
 * longer than the longest run inside the content, so a reply that itself
 * contains fenced code can't break out of the wrapper.
 */
function asMarkdownCodeFence(source: string): string {
  const longestRun = Math.max(0, ...(source.match(/`+/g) ?? []).map(run => run.length));
  const fence = "`".repeat(Math.max(3, longestRun + 1));
  return `${fence}markdown\n${source}\n${fence}`;
}

/** Extra per-part behavior threaded from the transcript's host seams. */
interface AttachmentBehavior {
  /** Live processing snapshots keyed by upload id (T4). */
  attachmentStatuses?: ReadonlyMap<string, MagnusChatProcessingSnapshot>;
  /** Host handler replacing the default click-to-open (T6). */
  onAttachmentActivate?: (part: MessagePart) => void;
  /** Retries tier-2 processing for a failed upload (T4). */
  onRetryProcessing?: (uploadId: string) => void;
  /** The block's default preview (P9); null outside `MagnusChat`. */
  preview: ReturnType<typeof useAttachmentPreview>;
}

/**
 * Map one attachment part to tile props, mirroring the flagship's
 * `MessagePartsAttachments` mapping:
 *   - `file` parts carry the blob URL → image thumbs + click-to-open
 *   - `data-magnus-document` parts carry no URL → static icon tile, or a
 *     click-to-preview when the uploads source can resolve the URL
 * A host `onAttachmentActivate` replaces the click. Otherwise, inside
 * `MagnusChat`, the block's lightweight preview opens (images and PDFs
 * inline; other `file` parts keep the open-in-a-new-tab default).
 * The processing dot prefers the live snapshot from the host's
 * `attachmentStatuses` map, falling back to the send-time snapshot on the
 * part's data payload; a failed live snapshot gains a retry affordance
 * when the host supplies `onRetryProcessing`.
 */
function attachmentTileProps(part: MessagePart, index: number, behavior: AttachmentBehavior): {
  key: string;
} & SentAttachmentTileProps {
  const {
    attachmentStatuses,
    onAttachmentActivate,
    onRetryProcessing,
    preview
  } = behavior;
  const activate = onAttachmentActivate ? () => onAttachmentActivate(part) : undefined;
  if (part.type === "data-magnus-document") {
    const data = part.data as {
      filename?: string;
      mediaType?: string;
      processingStatus?: string | null;
      uploadId?: string;
    };
    const live = data.uploadId ? attachmentStatuses?.get(data.uploadId) : undefined;
    const failed = live ? isProcessingFailed(live) : false;
    const {
      resolveUrl
    } = preview ?? {};
    const uploadId = data.uploadId;
    // Persisted document parts drop the blob URL, so the preview looks it up
    // on click — lazily, so a long transcript doesn't resolve every file.
    const previewDocument = preview && resolveUrl && uploadId ? () => preview.open({
      mediaType: data.mediaType,
      name: data.filename ?? "Document",
      resolveUrl: () => resolveUrl(uploadId)
    }) : undefined;
    return {
      key: data.uploadId ?? `doc-${index}`,
      mediaType: data.mediaType,
      name: data.filename ?? "Document",
      onActivate: activate ?? previewDocument,
      onRetry: failed && onRetryProcessing && data.uploadId ? () => onRetryProcessing(data.uploadId as string) : undefined,
      processingStatus: indicatorStatus(failed, live?.status, data)
    };
  }
  const file = part as {
    data?: {
      processingStatus?: string | null;
      uploadId?: string;
    };
    filename?: string;
    mediaType?: string;
    url?: string;
  };
  const url = file.url || undefined;
  const live = file.data?.uploadId ? attachmentStatuses?.get(file.data.uploadId) : undefined;
  const failed = live ? isProcessingFailed(live) : false;
  const name = file.filename ?? "Attachment";
  let openFile: (() => void) | undefined;
  if (url && preview && isPreviewableMediaType(file.mediaType)) {
    openFile = () => preview.open({
      mediaType: file.mediaType,
      name,
      url
    });
  } else if (url) {
    openFile = () => window.open(url, "_blank", "noopener,noreferrer");
  }
  return {
    key: file.data?.uploadId ?? `file-${index}`,
    mediaType: file.mediaType,
    name,
    onActivate: activate ?? openFile,
    onRetry: failed && onRetryProcessing && file.data?.uploadId ? () => onRetryProcessing(file.data?.uploadId as string) : undefined,
    previewUrl: file.mediaType?.startsWith("image/") ? url : undefined,
    processingStatus: indicatorStatus(failed, live?.status, file.data)
  };
}

/**
 * Section break for a `data-compaction-summary` part (ADR-054 §UI): an
 * `in-flight` shimmer while the summarizer runs, a labeled divider once
 * complete, nothing when the compaction was skipped.
 */
function CompactionSummarySegment(t0) {
  const $ = _c(2);
  const {
    data
  } = t0;
  if (data.status === "skipped") {
    return null;
  }
  if (data.status === "in-flight") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="my-4 flex items-center justify-center gap-2 text-muted-foreground text-xs"><Shimmer as="span">Summarizing chat context</Shimmer></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="my-6 flex items-center gap-3" role="separator"><Separator className="flex-1" /><span className="whitespace-nowrap text-[11px] text-muted-foreground/70">Chat context summarized</span><Separator className="flex-1" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * A user turn that mentions a skill or artifact, rendered as one continuous
 * run of prose with the chips embedded where they were typed. The segment
 * loop is geared toward assistant output (text → tools → text → …) and would
 * break each chip onto its own line outside the bubble, which reads as if
 * something other than the user said it.
 */
function UserInlineBody(t0) {
  const $ = _c(5);
  const {
    parts
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(USER_BUBBLE, "text-sm leading-relaxed");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== parts) {
    t2 = parts.map(_temp);
    $[1] = parts;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className={t1}><span className="whitespace-pre-wrap break-words">{t2}</span></div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * One transcript message. User turns render as a right-aligned bubble;
 * assistant turns render their parts full-width in order: `text` through
 * the same Streamdown + chiclet pipeline the Magnus web app uses (see
 * {@link MessageResponse}), `reasoning` collapsed behind a disclosure,
 * tool parts grouped into a timeline via {@link ToolPart}, and
 * skill/artifact reference parts as static chips. Attachments (`file` /
 * `data-magnus-document`) render ABOVE the bubble via
 * {@link SentAttachmentTile} — image thumbs and labeled file cards, not the
 * composer's staged tiles — right-aligned with the bubble on user turns,
 * matching the flagship web transcript. Compaction
 * summary parts render as a bare section break and continuation rows
 * render nothing (ADR-054). Unknown part types render nothing so newer
 * servers stay forward-compatible with installed copies of this block.
 * Assistant turns get a Copy / feedback / View-markdown action row — hidden
 * while the message is still streaming (`streaming`), pinned open on the
 * newest assistant turn and on any row the caller has voted on, and
 * hover-revealed everywhere else, matching the web app.
 */
function _temp(part, index) {
  const key = `${part.type}-${index}`;
  if (part.type === "text") {
    return part.text ? <span key={key}>{part.text}</span> : null;
  }
  if (part.type === "data-skill-reference") {
    return <MessageSkillReference className="mx-0.5 inline-flex align-middle" icon={<SkillsIcon />} key={key} skillName={part.data.skillName || "Skill"} />;
  }
  if (part.type === "data-artifact-reference") {
    return <MessageArtifactReference artifactName={part.data.artifactName || "Artifact"} className="mx-0.5 inline-flex align-middle" icon={<AppWindowIcon />} key={key} />;
  }
  return null;
}
export const Message = memo(function Message(t0) {
  const $ = _c(55);
  const {
    attachmentStatuses,
    feedback,
    isLastAssistant: t1,
    message,
    onAttachmentActivate,
    onRetryProcessing,
    renderMessageDebug,
    renderMessageMenuLabel,
    renderMessageMeta,
    renderUserMessage,
    smoothTextEnabled: t2,
    streaming: t3
  } = t0;
  const isLastAssistant = t1 === undefined ? false : t1;
  const smoothTextEnabled = t2 === undefined ? true : t2;
  const streaming = t3 === undefined ? false : t3;
  const [markdownView, setMarkdownView] = useState(false);
  const preview = useAttachmentPreview();
  const isUser = message.role === "user";
  const toolRegistry = useToolRegistry(useExtraToolViews());
  const t4 = message.parts;
  let markdownSource;
  let pinActions;
  let summaryOnly;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== attachmentStatuses || $[1] !== feedback || $[2] !== isLastAssistant || $[3] !== isUser || $[4] !== markdownView || $[5] !== message || $[6] !== onAttachmentActivate || $[7] !== onRetryProcessing || $[8] !== preview || $[9] !== renderMessageDebug || $[10] !== renderUserMessage || $[11] !== smoothTextEnabled || $[12] !== streaming || $[13] !== toolRegistry) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const attachmentParts = t4.filter(isAttachmentPart);
      const bodyParts = message.parts.filter(_temp2);
      const visibleBodyParts = bodyParts.filter(_temp3);
      if (isUser && message.parts.some(isBookingFormPart)) {
        t9 = null;
        break bb0;
      }
      if (visibleBodyParts.length === 0 && attachmentParts.length === 0) {
        t9 = null;
        break bb0;
      }
      const behavior = {
        attachmentStatuses,
        onAttachmentActivate,
        onRetryProcessing,
        preview
      };
      summaryOnly = visibleBodyParts.length > 0 && visibleBodyParts.every(isCompactionSummaryPart);
      const voiceSession = isUser ? undefined : visibleBodyParts.find(isVoiceSessionPart);
      if (voiceSession) {
        let t10;
        if ($[22] !== message || $[23] !== renderMessageDebug) {
          t10 = renderMessageDebug?.(message);
          $[22] = message;
          $[23] = renderMessageDebug;
          $[24] = t10;
        } else {
          t10 = $[24];
        }
        t9 = <div className="flex w-full min-w-0 flex-col"><VoiceSessionChip data={voiceSession.data} feedback={feedback} />{t10}</div>;
        break bb0;
      }
      pinActions = isLastAssistant || feedback?.vote !== undefined;
      const hasText = visibleBodyParts.some(_temp4);
      markdownSource = !isUser && hasText && !streaming ? markdownForCopy(message) : "";
      const inlineUserBody = isUser && visibleBodyParts.some(isReferencePart);
      let t10;
      if ($[25] !== isUser || $[26] !== message || $[27] !== renderUserMessage) {
        t10 = isUser ? renderUserMessage?.(message) : null;
        $[25] = isUser;
        $[26] = message;
        $[27] = renderUserMessage;
        $[28] = t10;
      } else {
        t10 = $[28];
      }
      const customUserBody = t10;
      const segments = groupMessageParts(visibleBodyParts, toolRegistry);
      const activeToolGroupIdx = streaming ? findActiveToolGroupIndex(segments) : -1;
      const t11 = isUser && "is-user items-end";
      if ($[29] !== t11) {
        t5 = cn("group flex w-full min-w-0 flex-col gap-2", t11);
        $[29] = t11;
        $[30] = t5;
      } else {
        t5 = $[30];
      }
      if ($[31] !== isUser || $[32] !== message.metadata?.voiceSessionId) {
        t6 = isUser && message.metadata?.voiceSessionId != null && <span className="-mb-1 flex items-center gap-1 text-muted-foreground text-xs"><AudioLinesIcon aria-hidden={true} className="size-3" />via voice agent</span>;
        $[31] = isUser;
        $[32] = message.metadata?.voiceSessionId;
        $[33] = t6;
      } else {
        t6 = $[33];
      }
      t7 = attachmentParts.length > 0 && <MessageAttachmentsScroll>{attachmentParts.map((part_2, index) => {
          const {
            key,
            ...props
          } = attachmentTileProps(part_2, index, behavior);
          return <SentAttachmentTile key={key} {...props} />;
        })}</MessageAttachmentsScroll>;
      t8 = markdownView && markdownSource ? <div className="w-full min-w-0 break-words text-sm leading-relaxed"><MessageResponse>{asMarkdownCodeFence(markdownSource)}</MessageResponse></div> : customUserBody == null ? inlineUserBody ? <UserInlineBody parts={visibleBodyParts} /> : segments.map((segment, segIdx) => {
        if (segment.type === "tool-group") {
          const hasTools = segment.items.some(_temp5);
          if (!hasTools) {
            const first = segment.items[0];
            return <Fragment key={`${message.id}-reasoning-group-${first?.kind === "reasoning" ? first.index : 0}`}>{segment.items.map(item_0 => item_0.kind === "reasoning" ? <StandaloneReasoning isStreamActive={streaming} key={`${message.id}-reasoning-${item_0.index}`} part={item_0.part} /> : null)}</Fragment>;
          }
          const firstItem = segment.items[0];
          const soleTool = segment.items.length === 1 && firstItem?.kind === "tool" ? firstItem : null;
          if (soleTool) {
            const {
              part: part_3,
              index: index_0
            } = soleTool;
            const isMidFlightCancelled = !streaming && (part_3.state === "input-streaming" || part_3.state === "input-available");
            if (isMidFlightCancelled) {
              return null;
            }
            return <ToolPart key={`${message.id}-tool-${index_0}`} part={part_3} streaming={streaming} />;
          }
          return <CollapsibleToolGroup isStreamActive={streaming && segIdx === activeToolGroupIdx} items={segment.items} key={`${message.id}-toolgroup-${segment.items.find(_temp6)?.part.toolCallId}`} registry={toolRegistry} />;
        }
        return <Part isUser={isUser} key={`${message.id}-${segment.index}`} part={segment.part} smoothTextEnabled={smoothTextEnabled} streaming={streaming} />;
      }) : customUserBody;
    }
    $[0] = attachmentStatuses;
    $[1] = feedback;
    $[2] = isLastAssistant;
    $[3] = isUser;
    $[4] = markdownView;
    $[5] = message;
    $[6] = onAttachmentActivate;
    $[7] = onRetryProcessing;
    $[8] = preview;
    $[9] = renderMessageDebug;
    $[10] = renderUserMessage;
    $[11] = smoothTextEnabled;
    $[12] = streaming;
    $[13] = toolRegistry;
    $[14] = markdownSource;
    $[15] = pinActions;
    $[16] = summaryOnly;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    markdownSource = $[14];
    pinActions = $[15];
    summaryOnly = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[34] !== feedback || $[35] !== isUser || $[36] !== markdownSource || $[37] !== markdownView || $[38] !== message || $[39] !== pinActions || $[40] !== renderMessageMenuLabel || $[41] !== renderMessageMeta || $[42] !== streaming || $[43] !== summaryOnly) {
    t10 = !streaming && !summaryOnly && (!isUser || renderMessageMeta != null) && <div className={cn("flex items-center gap-2 transition-opacity", !pinActions && "max-sm:opacity-100 sm:opacity-0 sm:focus-within:opacity-100 sm:group-hover:opacity-100")}>{renderMessageMeta?.(message)}<MessageActions feedback={feedback} markdownView={markdownView} menuLabel={isUser ? null : renderMessageMenuLabel?.(message)} message={message} onToggleMarkdown={markdownSource ? () => setMarkdownView(_temp7) : undefined} /></div>;
    $[34] = feedback;
    $[35] = isUser;
    $[36] = markdownSource;
    $[37] = markdownView;
    $[38] = message;
    $[39] = pinActions;
    $[40] = renderMessageMenuLabel;
    $[41] = renderMessageMeta;
    $[42] = streaming;
    $[43] = summaryOnly;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== message || $[46] !== renderMessageDebug) {
    t11 = renderMessageDebug?.(message);
    $[45] = message;
    $[46] = renderMessageDebug;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t5 || $[51] !== t6 || $[52] !== t7 || $[53] !== t8) {
    t12 = <div className={t5}>{t6}{t7}{t8}{t10}{t11}</div>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t5;
    $[51] = t6;
    $[52] = t7;
    $[53] = t8;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  return t12;
});
function Part(t0) {
  const $ = _c(20);
  const {
    isUser,
    part,
    smoothTextEnabled,
    streaming
  } = t0;
  if (isCompactionSummaryPart(part)) {
    let t1;
    if ($[0] !== part) {
      t1 = (part as {
        data?: {
          status?: string;
        };
      }).data ?? {};
      $[0] = part;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <CompactionSummarySegment data={t1} />;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (isCompactionContinuationPart(part)) {
    return null;
  }
  if (part.type === "text") {
    if (!part.text) {
      return null;
    }
    if (!isUser && streaming && smoothTextEnabled) {
      let t1;
      if ($[4] !== part.text) {
        t1 = <div className="break-words text-sm leading-relaxed"><SmoothTextBlock streaming={true} text={part.text} /></div>;
        $[4] = part.text;
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      return t1;
    }
    const t1 = isUser && USER_BUBBLE;
    let t2;
    if ($[6] !== t1) {
      t2 = cn("break-words text-sm leading-relaxed", t1);
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== isUser || $[9] !== part.text) {
      t3 = <MessageResponse hardLineBreaks={isUser}>{part.text}</MessageResponse>;
      $[8] = isUser;
      $[9] = part.text;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t2 || $[12] !== t3) {
      t4 = <div className={t2}>{t3}</div>;
      $[11] = t2;
      $[12] = t3;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  if (part.type === "data-skill-reference") {
    let t1;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <SkillsIcon />;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    const t2 = part.data.skillName || "Skill";
    let t3;
    if ($[15] !== t2) {
      t3 = <MessageSkillReference className="inline-flex self-start" icon={t1} skillName={t2} />;
      $[15] = t2;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  if (part.type === "data-artifact-reference") {
    const t1 = part.data.artifactName || "Artifact";
    let t2;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AppWindowIcon />;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    let t3;
    if ($[18] !== t1) {
      t3 = <MessageArtifactReference artifactName={t1} className="inline-flex self-start" icon={t2} />;
      $[18] = t1;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    return t3;
  }
  return null;
}
function _temp2(part) {
  return !isAttachmentPart(part);
}
function _temp3(part_0) {
  return !isCompactionContinuationPart(part_0);
}
function _temp4(part_1) {
  return part_1.type === "text" && part_1.text;
}
function _temp5(item) {
  return item.kind === "tool";
}
function _temp6(item_1) {
  return item_1.kind === "tool";
}
function _temp7(shown) {
  return !shown;
}